import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { ArrowLeft, Users, CheckCircle2, UserPlus, Search, X } from 'lucide-react';
import { format } from 'date-fns';
import { getApiUrl } from '@/lib/utils';

const API_URL = getApiUrl();

function SaturdayTeamSelection() {
  const { matchId } = useParams();
  const navigate = useNavigate();
  const [match, setMatch] = useState(null);
  const [players, setPlayers] = useState([]);
  const [selectedPlayers, setSelectedPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddPlayer, setShowAddPlayer] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [addingPlayer, setAddingPlayer] = useState(false);

  useEffect(() => {
    fetchData();
  }, [matchId]);

  const fetchData = async () => {
    try {
      const [matchRes, usersRes] = await Promise.all([
        axios.get(`${API_URL}/api/matches/${matchId}`, {
          withCredentials: true,
          headers: { Authorization: `Bearer ${localStorage.getItem('session_token')}` }
        }),
        axios.get(`${API_URL}/api/users/all`, {
          withCredentials: true,
          headers: { Authorization: `Bearer ${localStorage.getItem('session_token')}` }
        })
      ]);

      const matchData = matchRes.data;
      setMatch(matchData);
      setAllUsers(usersRes.data);

      // Get all players who RSVP'd IN
      const inRsvps = matchData.rsvps?.filter(r => r.status === 'in') || [];
      setPlayers(inRsvps);
      setSelectedPlayers(matchData.selected_players || []);
    } catch (error) {
      toast.error('Failed to load match');
      navigate('/admin');
    } finally {
      setLoading(false);
    }
  };

  const togglePlayer = (playerId) => {
    if (selectedPlayers.includes(playerId)) {
      setSelectedPlayers(selectedPlayers.filter(id => id !== playerId));
    } else {
      if (selectedPlayers.length >= 13) {
        toast.error('Maximum 13 players allowed for Saturday match');
        return;
      }
      setSelectedPlayers([...selectedPlayers, playerId]);
    }
  };

  const handleAddPlayer = async (userId) => {
    setAddingPlayer(true);
    try {
      await axios.post(
        `${API_URL}/api/matches/${matchId}/admin-add-and-select/${userId}`,
        {},
        {
          withCredentials: true,
          headers: { Authorization: `Bearer ${localStorage.getItem('session_token')}` }
        }
      );
      toast.success('Player added and selected!');
      setShowAddPlayer(false);
      setSearchQuery('');
      await fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add player');
    } finally {
      setAddingPlayer(false);
    }
  };

  const handleSave = async () => {
    if (selectedPlayers.length === 0) {
      toast.error('Please select at least one player');
      return;
    }

    try {
      await axios.put(
        `${API_URL}/api/matches/${matchId}/select-players`,
        { player_ids: selectedPlayers },
        {
          withCredentials: true,
          headers: { Authorization: `Bearer ${localStorage.getItem('session_token')}` }
        }
      );
      toast.success(`${selectedPlayers.length} players selected!`);
      navigate('/admin');
    } catch (error) {
      toast.error('Failed to save selection');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#E63946] mx-auto"></div>
          <p className="mt-4 text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Users who have RSVP'd IN
  const rsvpUserIds = players.map(p => p.user_id);
  
  // Users who can be added (not already RSVP'd IN)
  const availableToAdd = allUsers.filter(u => !rsvpUserIds.includes(u.user_id));
  
  // Filter by search
  const filteredUsers = availableToAdd.filter(u => 
    u.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.phone?.includes(searchQuery) ||
    u.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const unselectedPlayers = players.filter(p => !selectedPlayers.includes(p.user_id));

  return (
    <div className="min-h-screen bg-slate-50 pb-6">
      {/* Header */}
      <div className="sticky top-0 z-50 bg-[#0F172A] text-white pt-[env(safe-area-inset-top)]">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              onClick={() => navigate('/admin')}
              variant="ghost"
              size="icon"
              className="text-white hover:bg-white/10"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-lg font-bold">SATURDAY TEAM SELECTION</h1>
              <p className="text-sm text-slate-300">
                {match && format(new Date(match.date), 'dd MMM yyyy')} | {selectedPlayers.length}/13 selected
              </p>
            </div>
          </div>
          <Button
            onClick={handleSave}
            disabled={selectedPlayers.length === 0}
            className="bg-[#E63946] hover:bg-[#DC2626] text-white font-bold"
            data-testid="save-selection-btn"
          >
            Save Selection
          </Button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Add Player Button */}
        <Button
          onClick={() => setShowAddPlayer(true)}
          variant="outline"
          className="w-full border-dashed border-2 border-[#E63946] text-[#E63946] hover:bg-[#E63946]/5"
          data-testid="add-player-btn"
        >
          <UserPlus className="w-5 h-5 mr-2" />
          Add Player (Admin Override)
        </Button>

        {/* Add Player Modal */}
        {showAddPlayer && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl max-w-md w-full max-h-[80vh] overflow-hidden">
              <div className="p-4 border-b border-slate-200 flex items-center justify-between">
                <h3 className="font-bold text-lg">Add Player</h3>
                <Button
                  onClick={() => { setShowAddPlayer(false); setSearchQuery(''); }}
                  variant="ghost"
                  size="icon"
                  data-testid="close-add-player-modal"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>
              <div className="p-4">
                <div className="relative mb-4">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Search by name, phone, or email..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9"
                    data-testid="search-player-input"
                  />
                </div>
                <div className="max-h-60 overflow-y-auto space-y-2">
                  {filteredUsers.length === 0 ? (
                    <p className="text-center text-slate-500 py-4">No players found</p>
                  ) : (
                    filteredUsers.map(user => (
                      <div
                        key={user.user_id}
                        className="p-3 border border-slate-200 rounded-lg flex items-center justify-between hover:bg-slate-50"
                      >
                        <div>
                          <p className="font-semibold text-[#0F172A]">{user.name}</p>
                          <p className="text-xs text-slate-500">{user.cricket_role} • {user.phone}</p>
                        </div>
                        <Button
                          onClick={() => handleAddPlayer(user.user_id)}
                          size="sm"
                          disabled={addingPlayer || selectedPlayers.length >= 13}
                          className="bg-[#E63946] hover:bg-[#DC2626]"
                          data-testid={`add-user-${user.user_id}`}
                        >
                          <UserPlus className="w-4 h-4" />
                        </Button>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Selected Players */}
        <div className="bg-white rounded-xl shadow-sm border-2 border-green-500 p-5">
          <h2 className="text-xl font-bold text-[#0F172A] mb-4 flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-green-600" />
            Selected Players ({selectedPlayers.length}/13)
          </h2>
          {selectedPlayers.length === 0 ? (
            <p className="text-slate-500 text-center py-8">No players selected yet</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {players.filter(p => selectedPlayers.includes(p.user_id)).map((rsvp) => (
                <PlayerCard
                  key={rsvp.user_id}
                  player={rsvp.user}
                  selected={true}
                  onToggle={() => togglePlayer(rsvp.user_id)}
                />
              ))}
            </div>
          )}
        </div>

        {/* Available Players */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-5">
          <h2 className="text-xl font-bold text-[#0F172A] mb-4 flex items-center gap-2">
            <Users className="w-5 h-5" />
            Available Players ({unselectedPlayers.length})
          </h2>
          {unselectedPlayers.length === 0 ? (
            <p className="text-green-600 text-center py-8 font-semibold">All RSVP'd players selected!</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {unselectedPlayers.map((rsvp) => (
                <PlayerCard
                  key={rsvp.user_id}
                  player={rsvp.user}
                  selected={false}
                  onToggle={() => togglePlayer(rsvp.user_id)}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function PlayerCard({ player, selected, onToggle }) {
  return (
    <div
      className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
        selected
          ? 'bg-green-50 border-green-500 shadow-sm'
          : 'bg-slate-50 border-slate-200 hover:border-[#E63946]'
      }`}
      onClick={onToggle}
      data-testid={`player-card-${player?.user_id}`}
    >
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <p className="font-bold text-[#0F172A]">{player?.nickname || player?.name}</p>
          <p className="text-sm text-slate-600">{player?.cricket_role}</p>
          <p className="text-xs text-slate-500 mt-1">{player?.phone}</p>
        </div>
        <div>
          {selected ? (
            <CheckCircle2 className="w-8 h-8 text-green-600" />
          ) : (
            <div className="w-8 h-8 rounded-full border-2 border-slate-300"></div>
          )}
        </div>
      </div>
    </div>
  );
}

export default SaturdayTeamSelection;
